import { Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useOpenState } from '@shiftfinancial/react-hooks';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { CheckboxWithLabel } from 'formik-material-ui';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { GuarantorsList } from '.';
import AccountInfoHeader from '../../components/AccountInfoHeader';
import AlertError from '../../components/AlertError';
import { CheckOutSummary } from '../../components/CheckOutSummary';
import checkOutSummarySelectors from '../../components/CheckOutSummary/selectors';
import { Dialog } from '../../components/Dialog';
import PageLayout from '../../components/PageLayout';
import { CheckOut, Guarantor, RepaymentSchedule } from '../../types';
import accountSelectionSelectors from '../AccountSelection/selectors';
import RepaymentDetails from './RepaymentDetails';
import actions from './actions';
import { multiGuarantorsSchema, schema } from './validations';

const useStyles: any = makeStyles((theme: any) => ({
  confirmContainer: {
    padding: theme.spacing(2),
    '& button': {
      width: '100%',
    },
  },
  confirmContainerHavingMultipleButtons: {
    padding: theme.spacing(2),
    '& button': {
      width: '97%',
    },
  },
  rightButton: {
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      float: 'right',
      paddingLeft: theme.spacing(1),
    },
  },
  agreementContainer: {
    borderTop: `1px solid ${theme.palette.grey['300']}`,
    backgroundColor: theme.palette.grey['200'],
    padding: theme.spacing(3),
    '& h4': {
      paddingTop: theme.spacing(1.5),
    },
    '& p': {
      paddingBottom: theme.spacing(1),
    },
    '& span': {
      paddingBottom: theme.spacing(1.5),
    },
  },
  checkbox: {
    alignSelf: 'start',
    paddingTop: 1,
  },
  errorContainer: {
    marginBottom: theme.spacing(1),
  },
}));

const EquipmentFinanceCheckoutConfirmation = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const accounts = useSelector(accountSelectionSelectors.selectAccounts);
  const selectedAccount = useSelector(accountSelectionSelectors.selectSelectedAccount);
  const checkOut: CheckOut = useSelector(checkOutSummarySelectors.selectCheckOut);
  const [stateRepaymentSchedule, setRepaymentScheduleState] = useState<RepaymentSchedule>();
  const [stateGuarantors, setGuarantorsState] = useState<Guarantor[]>([]);
  const [hasMoreThanOneGuarantors, setHasMoreThanOneGuarantors] = useState(false);
  const [initiator, setInitiator] = useState<Guarantor>();
  const hasMutipleAccounts = accounts && accounts.length > 1;
  const showDeclineOption = checkOut && checkOut.buyerGcAccountId && !checkOut.redirectUrl;
  const checkoutDeclineDialogOpenState = useOpenState();

  const sortGuarantors = (guarantorsList: Guarantor[]) => {
    const initiator = guarantorsList.filter((f) => {
      return f.isInitiator;
    });
    const otherGuarantors = guarantorsList
      .filter((f) => {
        return !f.isInitiator;
      })
      .sort((a, b) => {
        return a.firstName > b.firstName ? 1 : -1;
      });
    setInitiator(initiator[0]);
    setHasMoreThanOneGuarantors(guarantorsList && guarantorsList.length > 1);
    setGuarantorsState(initiator.concat(otherGuarantors));
  };
  useEffect(() => {
    if (selectedAccount && checkOut) {
      dispatch(actions.getLoanAgreementDetails(checkOut.id, selectedAccount.gcAccountId)).then((response: any) => {
        const { payload, error } = response;
        if (!error && payload.data) {
          if (payload.data.guarantors) {
            sortGuarantors(payload.data.guarantors);
          }
          if (payload.data.repaymentSchedule) {
            setRepaymentScheduleState(payload.data.repaymentSchedule);
          }
        } else {
          history.push({
            pathname: '/500',
            state: { message: 'Sorry, this link is no longer valid' },
          });
        }
      });
    } else if (!selectedAccount) {
      handleBack();
    } else {
      history.push({
        pathname: '/500',
        state: { message: 'Sorry, this link is no longer valid' },
      });
    }
  }, [selectedAccount]);

  const handleBack = () => {
    history.push({
      pathname: '/select-account',
    });
  };

  const handlePay = (values: any, { setSubmitting }: FormikHelpers<any>) => {
    closeSnackbar();
    setSubmitting(true);
    if (!selectedAccount) {
      return;
    }
    dispatch(actions.confirmPay(checkOut.id, selectedAccount?.gcAccountId)).then((response: any) => {
      const { payload } = response;
      if (payload.response && payload.response.status !== 200) {
        if (payload.response.data && payload.response.data.message) {
          setSubmitting(false);
          enqueueSnackbar(payload.response.data.message, {
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            variant: 'error',
            key: 'shift',
            autoHideDuration: 12000,
          });
        } else {
          history.push({
            pathname: '/500',
          });
        }
      } else {
        history.push({
          pathname: '/success',
          state: { redirectUrl: checkOut.redirectUrl, isDecline: false },
        });
      }
    });
  };

  const handleDecline = () => {
    closeSnackbar();
    dispatch(actions.declineCheckOut(checkOut.id)).then((response: any) => {
      const { payload } = response;
      if (payload.response && payload.response.status !== 200) {
        if (payload.response.data && payload.response.data.message) {
          enqueueSnackbar(payload.response.data.message, {
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            variant: 'error',
            key: 'shift',
            autoHideDuration: 12000,
          });
        } else {
          history.push({
            pathname: '/500',
          });
        }
      } else {
        history.push({
          pathname: '/success',
          state: { isDecline: true },
        });
      }
    });
  };

  return (
    <PageLayout pageTitle='Purchase agreement'>
      {selectedAccount && <AccountInfoHeader name={selectedAccount.name} abn={selectedAccount.abn} />}
      <CheckOutSummary />
      {stateRepaymentSchedule && <RepaymentDetails repaymentSchedule={stateRepaymentSchedule} />}
      {stateGuarantors && <GuarantorsList guarantors={stateGuarantors} />}
      <Dialog
        description='Are you sure you want to decline this purchase?'
        title='Decline purchase'
        onConfirm={handleDecline}
        openState={checkoutDeclineDialogOpenState}
      ></Dialog>
      <Formik
        initialValues={{
          borrowerAcceptance: false,
          guarantorAcceptance1: false,
          guarantorAcceptance2: false,
        }}
        validationSchema={hasMoreThanOneGuarantors ? multiGuarantorsSchema : schema}
        validateOnBlur={false}
        onSubmit={handlePay}
      >
        {(formikProps) => {
          return (
            <Form data-testid='agreementForm'>
              <Grid className={classes.agreementContainer}>
                <Typography variant='subtitle1' component='p'>
                  This constitutes an offer by us (ACN 601 158 507 Pty Ltd ACN 601 158 507) (Lender) to enter into a Loan Agreement incorporating the
                  Terms and Conditions of the Equipment Line Facility – Master Agreement (Master Agreement) between us and the Borrower. You, the
                  Borrower, may accept this offer as provided for below and in accordance with the Terms and Conditions of the Master Agreement (the
                  Terms and Conditions). Terms defined in the Terms and Conditions of the Master Agreement have the same meaning in this agreement.
                </Typography>
                <Typography variant='subtitle2' component='h4'>
                  Borrower acceptance
                </Typography>
                <Field
                  component={CheckboxWithLabel}
                  name='borrowerAcceptance'
                  type='checkbox'
                  data-testid='uia-borrowerAcceptance'
                  labelPlacement='end'
                  className={classes.checkbox}
                  disableRipple
                  color='primary'
                  Label={{
                    label: (
                      <Typography variant='subtitle1' component='span'>
                        By ticking this box, I (as the Authorised Signatory of the Borrower) confirm that I have read and understood the Loan Schedule
                        and Master Agreement and that the Borrower agrees to enter into the offered Loan Agreement with the Lender on these terms by
                        my accepting this Loan Schedule. Under the Terms and Conditions, when you, as the Borrower, enter into the Loan Agreement by
                        accepting this Loan Schedule, the Borrower will be a Grantor and will grant us a Security Interest to secure payment of the
                        Secured Money in respect of this Loan Agreement on the terms set out in clause 9 of the Terms and Conditions.
                      </Typography>
                    ),
                  }}
                />
                <Typography variant='subtitle2' component='h4'>
                  Guarantor acceptance
                </Typography>
                <Typography variant='subtitle1' component='p'>
                  In consideration of us offering this Loan Agreement to the Borrower and entering into this Loan Agreement with the Borrower, each
                  Guarantor accepting this Loan Schedule agrees to guarantee the payment of the Secured Money in respect of this Loan Agreement on the
                  terms set out in clause 10 of the Terms and Conditions.
                </Typography>
                {initiator && (
                  <Field
                    component={CheckboxWithLabel}
                    name='guarantorAcceptance1'
                    type='checkbox'
                    data-testid='uia-guarantorAcceptance1'
                    labelPlacement='end'
                    className={classes.checkbox}
                    disableRipple
                    color='primary'
                    Label={{
                      label: (
                        <Typography variant='subtitle1' component='span'>
                          By ticking this box,{' '}
                          <b>
                            {initiator.firstName} {initiator.middleName || ''} {initiator.lastName}
                          </b>{' '}
                          agrees to guarantee the payment of the Secured Money in respect of this Loan Agreement on the terms set out in clause 10 of
                          the Terms and Conditions.
                        </Typography>
                      ),
                    }}
                  />
                )}
                {initiator && hasMoreThanOneGuarantors && (
                  <Field
                    component={CheckboxWithLabel}
                    name='guarantorAcceptance2'
                    type='checkbox'
                    data-testid='uia-guarantorAcceptance2'
                    labelPlacement='end'
                    className={classes.checkbox}
                    disableRipple
                    color='primary'
                    Label={{
                      label: (
                        <Typography variant='subtitle1' component='span'>
                          By ticking this box,{' '}
                          <b>
                            {initiator.firstName} {initiator.middleName || ''} {initiator.lastName}
                          </b>{' '}
                          agrees, as the authorised agent for and on behalf of each of the other named Guarantors, and separately under power of
                          attorney, that each such Guarantor separately guarantees the payment of the Secured Money in respect of this Loan Agreement
                          on the terms set out in clause 10 of the Terms and Conditions. By ticking this box the named agent and attorney for the
                          Guarantors also declares that at the time of this acceptance, the agent and attorney has no notice of the revocation of
                          their authority as the agent of the Guarantors or of their appointment as attorney.
                        </Typography>
                      ),
                    }}
                  />
                )}
                <Typography variant='subtitle1' component='p'>
                  Under the Terms and Conditions, when each Guarantor enters into this Loan Agreement as a guarantor, the Guarantor will be a Grantor
                  and will grant us a Security Interest to secure payment of the Secured Money in respect of this Loan Agreement on the terms set out
                  in clause 9 of the Terms and Conditions. Acceptance is provided on behalf of the Borrower and each Guarantor.
                </Typography>
              </Grid>
              <Grid container className={hasMutipleAccounts ? classes.confirmContainerHavingMultipleButtons : classes.confirmContainer}>
                {!checkoutDeclineDialogOpenState.isOpen && !formikProps.isValid && (
                  <Grid item xs={12} className={classes.errorContainer}>
                    <AlertError
                      message='You need to check all the boxes and agree to the purchase agreement to continue to pay.'
                      data-testid='uia-agreementError'
                    />
                  </Grid>
                )}
                {hasMutipleAccounts && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      size='large'
                      variant='outlined'
                      color='primary'
                      type='button'
                      disabled={formikProps.isSubmitting && !!selectedAccount}
                      data-testid='uia-goback'
                      onClick={() => handleBack()}
                    >
                      Go back
                    </Button>
                  </Grid>
                )}
                {!hasMutipleAccounts && showDeclineOption && (
                  <Grid item xs={12} sm={6}>
                    <Button size='large' variant='outlined' color='primary' data-testid='uia-decline' onClick={checkoutDeclineDialogOpenState.open}>
                      Decline
                    </Button>
                  </Grid>
                )}
                <Grid item className={classes.rightButton} xs={12} sm={hasMutipleAccounts || showDeclineOption ? 6 : 12}>
                  <Button
                    size='large'
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={formikProps.isSubmitting && !!selectedAccount}
                    data-testid='uia-pay'
                    onClick={() => {
                      formikProps.submitForm();
                    }}
                  >
                    Accept and Pay
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </PageLayout>
  );
};

export default EquipmentFinanceCheckoutConfirmation;
